import type { BacktestOutputs } from "@/api/types";
import { useFormatDate } from "@/hooks/useFormatDate";

const numberFormat = new Intl.NumberFormat("en-US", { maximumSignificantDigits: 10 });

type Props = {
  outputs?: BacktestOutputs | null;
  /** True while a run is in progress and no snapshot has arrived yet. */
  waiting?: boolean;
  className?: string;
};

/** Latest Pine `plot()` values from `result.outputs` (snapshot, not series history). */
export function PlotValuesTable({ outputs, waiting = false, className = "" }: Props) {
  const { dateTime } = useFormatDate();
  const plots = outputs?.plots ?? [];

  return (
    <div className={`flex min-h-0 flex-col gap-2 ${className}`}>
      {outputs?.time ? (
        <div className="shrink-0 text-xs text-[var(--text-muted)]">
          Values for candle: {dateTime(outputs.time)}
        </div>
      ) : null}
      <div className="min-h-0 flex-1 overflow-auto rounded-[12px] border border-[var(--border)]">
        <table className="dtable">
          <caption className="sr-only">Latest pinescript output values</caption>
          <thead>
            <tr>
              <th scope="col" className="w-10">
                #
              </th>
              <th scope="col">
                Title
              </th>
              <th scope="col" className="!text-right">
                Value
              </th>
            </tr>
          </thead>
          <tbody>
            {plots.length > 0 ? (
              plots.map((row, i) => (
                <tr
                  key={`${row.title}-${i}`}
                  className="border-t border-[var(--divider)] "
                >
                  <td className="px-3 py-2 tabular-nums text-[var(--text-muted)]">{i + 1}</td>
                  <td className="break-words px-3 py-2 text-[var(--text-primary)]">
                    {row.title || `Output ${i + 1}`}
                  </td>
                  <td
                    className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-[var(--text-primary)]"
                    title={row.value === null ? "Not available" : String(row.value)}
                  >
                    {row.value === null ? "—" : numberFormat.format(row.value)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className="px-3 py-8 text-center text-sm text-[var(--text-muted)]">
                  {waiting
                    ? "Waiting for historical replay to finish."
                    : !outputs
                      ? "No values available. Run the backtest to sample a completed candle."
                      : "This script has no pinescript outputs."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
